/**
 * Offline replay classification. Never treat error *text* as success —
 * "Illegal work-order transition: ASSIGNED -> COMPLETED" contains COMPLETED
 * and used to wipe local evidence (E-325).
 */
export type WoReplayClass = 'already_completed' | 'auth' | 'dead_letter' | 'failed';

const TERMINAL_STATUSES = new Set(['CANCELLED', 'ESCALATED']);

export function replayFailureMessage(err: unknown, fallback = 'Sync failed'): string {
  if (err instanceof Error && err.message.trim()) {
    return err.message.trim();
  }
  const raw = String(err ?? '').trim();
  return raw || fallback;
}

export function isAuthReplayError(err: unknown): boolean {
  return /401|unauthorized|session|login/i.test(replayFailureMessage(err));
}

/**
 * Branch on a structured outcome, not substrings like COMPLETED / 409 / not in progress.
 * `already_completed` only when the live work-order status is confirmed COMPLETED.
 */
export function classifyWoReplayFailure(input: {
  err: unknown;
  confirmedStatus?: string | null;
  createdAt?: string | null;
  nowMs?: number;
  ttlMs?: number;
}): WoReplayClass {
  if (isAuthReplayError(input.err)) {
    return 'auth';
  }
  const status = (input.confirmedStatus ?? '').toUpperCase();
  if (status === 'COMPLETED') {
    return 'already_completed';
  }
  if (TERMINAL_STATUSES.has(status)) {
    return 'dead_letter';
  }
  if (isPermanentClientError(input.err)) {
    return 'dead_letter';
  }
  if (isAgedOut(input.createdAt, input.nowMs, input.ttlMs)) {
    return 'dead_letter';
  }
  return 'failed';
}

export function isPermanentClientError(err: unknown): boolean {
  return /\b(400|404|409|422)\b/.test(replayFailureMessage(err));
}

function isAgedOut(createdAt: string | null | undefined, nowMs?: number, ttlMs?: number): boolean {
  if (!createdAt || ttlMs == null || ttlMs <= 0) {
    return false;
  }
  const created = Date.parse(createdAt);
  if (!Number.isFinite(created)) {
    return false;
  }
  return (nowMs ?? Date.now()) - created > ttlMs;
}
